import { useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GrainOverlay } from "../components/effects/GrainOverlay";
import { CustomCursor } from "../components/effects/CustomCursor";
import { BONUSES, HOST, luxStyle } from "../content";

const NEXT_STEPS = [
  { num: "01", title: "CHECK YOUR INBOX", desc: "Your confirmation email is on its way right now. If it's not there in a few minutes, check spam and mark us safe so your access link lands." },
  { num: "02", title: "WATCH FOR THE DATE", desc: "You're on the priority list — registrants hear the webinar date first, before it's announced anywhere else." },
  { num: "03", title: "JOIN LIVE & ASK", desc: "Your live access link arrives by email before the briefing. Bring your questions — live Q&A is included." },
  { num: "04", title: "DECIDE YOUR NEXT MOVE", desc: "After the briefing, optionally book a same-day 1-on-1 strategy call with Reginald. No credit pull, no obligation — ever." },
];

export default function ThankYou() {
  const [params] = useSearchParams();
  const firstName = params.get("name") || "";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="reg-app min-h-screen" style={luxStyle(2)} data-testid="thank-you-page">
      <GrainOverlay />
      <CustomCursor />
      <div className="relative max-w-4xl mx-auto px-6 sm:px-10 pt-20 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <div className="mono text-[13px] tracking-[0.35em] text-[#FFC631] mb-8">REG VA ADVANTAGE — REGISTRATION CONFIRMED</div>
          <div className="w-20 h-20 rounded-full border-2 border-[#FE0000] flex items-center justify-center text-[#FE0000] text-4xl mb-8" data-testid="thank-you-check">✓</div>
          <h1 className="serif font-black uppercase tracking-[-0.03em] text-[clamp(38px,5.5vw,72px)] text-white" style={{ lineHeight: 0.97 }} data-testid="thank-you-h1">
            {firstName ? `${firstName.toUpperCase()}, ` : ""}YOUR SEAT IS <span className="text-[#FE0000]">RESERVED.</span>
          </h1>
          <p className="text-[17px] text-white leading-relaxed mt-6 max-w-xl">
            You&apos;re on the priority list for the free 45-minute live briefing on VA home
            loans for veterans and active-duty service members in Texas. A confirmation email is heading to your
            inbox right now.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.8 }} className="mt-14">
          <div className="mono text-[13px] tracking-[0.3em] text-[#FFC631] mb-6">WHAT HAPPENS NEXT</div>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="thank-you-next-steps">
            {NEXT_STEPS.map((s) => (
              <div key={s.num} className="rounded-lg border border-white/10 bg-white/[0.02] p-6">
                <div className="mono text-xs text-[#FFC631] mb-3">{s.num}</div>
                <h3 className="serif font-black uppercase text-lg text-white mb-2">{s.title}</h3>
                <p className="text-sm text-white leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }} className="mt-14">
          <div className="rounded-xl border border-[#FE0000]/25 bg-[#FE0000]/[0.04] p-8" data-testid="thank-you-bonus-recap">
            <div className="mono text-[13px] tracking-[0.3em] text-[#FFC631] mb-6">EVERYTHING INCLUDED WITH YOUR SEAT</div>
            <ul className="space-y-3">
              {BONUSES.map((b, i) => (
                <li key={i} className="flex gap-3 text-[14px] text-white leading-snug">
                  <span className="text-[#FE0000] shrink-0">✓</span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45, duration: 0.8 }} className="mt-14 flex flex-wrap items-center gap-6">
          <Link
            to="/"
            data-testid="thank-you-home-link"
            data-cta
            className="border border-[#FFE100] text-[#FFE100] font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-md btn-slide"
          >
            ← BACK TO THE BRIEFING PAGE
          </Link>
          <div className="mono text-[13px] tracking-widest text-white">FREE EDUCATIONAL EVENT · NO CREDIT PULL · NO OBLIGATION</div>
        </motion.div>

        <div className="mt-20 pt-8 border-t border-white/10">
          <p className="mono text-[13px] text-white tracking-wider">
            {HOST.name} · {HOST.company} · NMLS# {HOST.nmls} · Licensed in {HOST.states}
          </p>
        </div>
      </div>
    </div>
  );
}
